import { RoutesEnum } from '@/enums/routes.enum';
import { AuthProviderEnum } from '@repo/common/enums';
import { BookingCountryService, BookingShop } from '@repo/common/interfaces';
import { isRemote } from '@repo/common/helpers';

export enum StepIdEnum {
  CATEGORIES = 'categories',
  SERVICES = 'services',
  STORES = 'stores',
  TIME_SLOTS = 'timeSlots',
  USER = 'user',
  INFORMATIONS = 'informations',
  AUTH = 'auth',
  CONFIRM = 'confirm',
  SUCCESS = 'success',
}

export enum StepsFlowEnum {
  SERVICE = 'service',
  STORE_LOCATOR = 'storeLocator',
  STANDALONE = 'standalone',
  SUPER_MODEL = 'superModel',
  BOOK_FOR_OTHER = 'bookForOther',
  BOOK_FOR_OTHER_WITH_STORE = 'bookForOtherWithStore',
}

export enum NestedStatusEnum {
  NESTED = 'nested',
}

export interface StepInterface {
  id: StepIdEnum;
  path: string;
  stepperOptions?: {
    title?: string;
    reassurance?: {
      service?: boolean;
      shop?: boolean;
      timeSlot?: boolean;
    };
  };
  protected?: boolean;
  checkSession?: boolean;
  hasToSkip?: ({
    countryService,
  }: {
    countryService?: BookingCountryService;
    shop?: BookingShop;
  }) => boolean;
}

export interface StepConfigInterface {
  type: StepsFlowEnum;
  providerId: AuthProviderEnum;
  steps: StepInterface[];
}

export const serviceFlow: StepConfigInterface = {
  type: StepsFlowEnum.SERVICE,
  providerId: AuthProviderEnum.CONNECT,
  steps: [
    {
      id: StepIdEnum.INFORMATIONS,
      path: RoutesEnum.INFORMATIONS,
      stepperOptions: {
        title: `Additional informations`,
        reassurance: {
          service: true,
        },
      },
    },
    {
      id: StepIdEnum.STORES,
      path: RoutesEnum.STORES,
      stepperOptions: {
        title: `Store`,
        reassurance: {
          service: true,
        },
      },
      hasToSkip: skipStoreSelectionStep,
    },
    {
      id: StepIdEnum.TIME_SLOTS,
      path: RoutesEnum.TIME_SLOTS,
      stepperOptions: {
        title: `Time slot`,
        reassurance: {
          service: true,
          shop: true,
        },
      },
    },
  ],
};

export const storeLocatorFlow: StepConfigInterface = {
  type: StepsFlowEnum.STORE_LOCATOR,
  providerId: AuthProviderEnum.CONNECT,
  steps: [
    {
      id: StepIdEnum.CATEGORIES,
      path: RoutesEnum.CATEGORIES,
      stepperOptions: {
        title: `Category`,
        reassurance: {
          shop: true,
        },
      },
    },
    {
      id: StepIdEnum.SERVICES,
      path: RoutesEnum.SERVICES,
      stepperOptions: {
        title: `Service`,
        reassurance: {
          shop: true,
        },
      },
    },
    {
      id: StepIdEnum.INFORMATIONS,
      path: RoutesEnum.INFORMATIONS,
      stepperOptions: {
        title: `Additional informations`,
        reassurance: {
          service: true,
          shop: true,
        },
      },
    },
    {
      id: StepIdEnum.TIME_SLOTS,
      path: RoutesEnum.TIME_SLOTS,
      stepperOptions: {
        title: `Time slot`,
        reassurance: {
          service: true,
          shop: true,
        },
      },
    },
  ],
};

export const defaultFlow: StepConfigInterface = {
  type: StepsFlowEnum.STANDALONE,
  providerId: AuthProviderEnum.CONNECT,
  steps: [
    {
      id: StepIdEnum.CATEGORIES,
      path: RoutesEnum.CATEGORIES,
      stepperOptions: {
        title: `Category`,
      },
    },
    {
      id: StepIdEnum.SERVICES,
      path: RoutesEnum.SERVICES,
      stepperOptions: {
        title: 'Service',
      },
    },
    {
      id: StepIdEnum.INFORMATIONS,
      path: RoutesEnum.INFORMATIONS,
      stepperOptions: {
        title: `Additional informations`,
        reassurance: {
          service: true,
        },
      },
    },
    {
      id: StepIdEnum.STORES,
      path: RoutesEnum.STORES,
      stepperOptions: {
        title: 'Store',
        reassurance: {
          service: true,
        },
      },
      hasToSkip: skipStoreSelectionStep,
    },
    {
      id: StepIdEnum.TIME_SLOTS,
      path: RoutesEnum.TIME_SLOTS,
      stepperOptions: {
        title: 'Time slot',
        reassurance: {
          service: true,
          shop: true,
        },
      },
    },
  ],
};

export const superModelFlow: StepConfigInterface = {
  type: StepsFlowEnum.SUPER_MODEL,
  providerId: AuthProviderEnum.CONNECT,
  steps: [
    {
      id: StepIdEnum.SERVICES,
      path: RoutesEnum.SERVICES,
      stepperOptions: {
        title: 'Service',
      },
    },
    {
      id: StepIdEnum.INFORMATIONS,
      path: RoutesEnum.INFORMATIONS,
      stepperOptions: {
        title: `Additional informations`,
        reassurance: {
          service: true,
        },
      },
    },
    {
      id: StepIdEnum.STORES,
      path: RoutesEnum.STORES,
      stepperOptions: {
        title: 'Store',
        reassurance: {
          service: true,
        },
      },
      hasToSkip: skipStoreSelectionStep,
    },
    {
      id: StepIdEnum.TIME_SLOTS,
      path: RoutesEnum.TIME_SLOTS,
      stepperOptions: {
        title: 'Time slot',
        reassurance: {
          service: true,
          shop: true,
        },
      },
    },
  ],
};

export const bookForOtherFlow: StepConfigInterface = {
  type: StepsFlowEnum.BOOK_FOR_OTHER,
  providerId: AuthProviderEnum.FED,
  steps: [
    {
      id: StepIdEnum.CATEGORIES,
      path: RoutesEnum.CATEGORIES,
      stepperOptions: {
        title: `Category`,
      },
      protected: true,
    },
    {
      id: StepIdEnum.SERVICES,
      path: RoutesEnum.SERVICES,
      stepperOptions: {
        title: 'Service',
      },
      protected: true,
    },
    {
      id: StepIdEnum.INFORMATIONS,
      path: RoutesEnum.INFORMATIONS,
      stepperOptions: {
        title: `Additional informations`,
        reassurance: {
          service: true,
        },
      },
      protected: true,
    },
    {
      id: StepIdEnum.STORES,
      path: RoutesEnum.STORES,
      stepperOptions: {
        title: 'Store',
        reassurance: {
          service: true,
        },
      },
      protected: true,
      hasToSkip: skipStoreSelectionStep,
    },
    {
      id: StepIdEnum.TIME_SLOTS,
      path: RoutesEnum.TIME_SLOTS,
      stepperOptions: {
        title: 'Time slot',
        reassurance: {
          service: true,
          shop: true,
        },
      },
      protected: true,
    },
  ],
};

export const bookForOtherWithStoreFlow: StepConfigInterface = {
  type: StepsFlowEnum.BOOK_FOR_OTHER_WITH_STORE,
  providerId: AuthProviderEnum.FED,
  steps: [
    {
      id: StepIdEnum.CATEGORIES,
      path: RoutesEnum.CATEGORIES,
      stepperOptions: {
        title: `Category`,
        reassurance: {
          shop: true,
        },
      },
      protected: true,
    },
    {
      id: StepIdEnum.SERVICES,
      path: RoutesEnum.SERVICES,
      stepperOptions: {
        title: 'Service',
        reassurance: {
          shop: true,
        },
      },
      protected: true,
    },
    {
      id: StepIdEnum.INFORMATIONS,
      path: RoutesEnum.INFORMATIONS,
      stepperOptions: {
        title: `Additional informations`,
        reassurance: {
          service: true,
          shop: true,
        },
      },
      protected: true,
    },
    {
      id: StepIdEnum.TIME_SLOTS,
      path: RoutesEnum.TIME_SLOTS,
      stepperOptions: {
        title: 'Time slot',
        reassurance: {
          service: true,
          shop: true,
        },
      },
      protected: true,
    },
  ],
};

function skipStoreSelectionStep({
  countryService,
}: {
  countryService?: BookingCountryService;
}): boolean {
  return isRemote(countryService);
}
